import {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState,
} from "react";
import { listClients } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Super-admin "View as client" tenant switch.
 *
 * The default scope is "all tenants" for super_admin (no filter), or
 * whatever client(s) the user is assigned to for everyone else. When a
 * super_admin selects a client from the top-nav picker, we persist the
 * choice in localStorage and expose it as `viewAsClientId` for every
 * query that wants to scope itself.
 *
 * Components that fetch tenant-scoped data should call
 * `useViewAsClient()` and apply the filter themselves (RLS already
 * enforces it for non-super-admins; for super_admin we apply it
 * client-side because they see everything by default).
 */
const Ctx = createContext({
  viewAsClientId: "",
  setViewAsClientId: () => {},
  clients: [],
  isSuperAdmin: false,
  activeClient: null,
});

const KEY = "timeslot.view_as_client_id";

export function ViewAsClientProvider({ children }) {
  const { session, roles } = useAuth();
  const user = session?.user;
  const isSuperAdmin = (roles || []).includes("super_admin");

  const [viewAsClientId, setRaw] = useState(() => {
    try { return localStorage.getItem(KEY) || ""; } catch { return ""; }
  });
  const [clients, dispatchClients] = useReducer((_state, action) => action.payload, []);

  // Load clients list for the picker (super_admin only).
  useEffect(() => {
    let cancelled = false;
    if (!isSuperAdmin) {
      dispatchClients({ payload: [] });
      return;
    }
    (async () => {
      const { data } = await listClients();
      if (!cancelled) dispatchClients({ payload: data || [] });
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin, user?.id]);

  const setViewAsClientId = useCallback((id) => {
    setRaw(id);
    try {
      if (id) localStorage.setItem(KEY, id);
      else    localStorage.removeItem(KEY);
    } catch (e) { /* ignore */ }
  }, []);

  const activeClient = useMemo(
    () => clients.find((c) => c.id === viewAsClientId) || null,
    [clients, viewAsClientId]
  );

  const value = useMemo(
    () => ({ viewAsClientId, setViewAsClientId, clients, isSuperAdmin, activeClient }),
    [viewAsClientId, setViewAsClientId, clients, isSuperAdmin, activeClient]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useViewAsClient() {
  return useContext(Ctx);
}
